import { RangeEntryWrapper } from '../../Types'
import { EMPTY_STRING } from '../../../../../Constants/Separators'

/**
 * Combines sequential numbers into a single range.
 * Excludes and includes are treated separately.
 * Input order does not matter.
 *
 * @param ranges - unsorted range entry wrappers
 *
 * @returns - sorted range entry wrappers
 */
export const combineSequentialNumbersIntoRanges = (ranges: RangeEntryWrapper[]): RangeEntryWrapper[] =>
    ranges.reduce((accum: RangeEntryWrapper[], next: RangeEntryWrapper): RangeEntryWrapper[] => {
        // Check if the accumulator is empty or next is a range/string.
        if (accum.length === 0 || next.isRange || typeof next.value !== 'number') {
            accum.push(next)

            return accum
        }

        // Check if the next value fits into any range in the accumulator or is sequential to any range or number in the accumulator.
        let isSequential: boolean = false

        const mappedAccum: RangeEntryWrapper[] = accum.map((accumEntry: RangeEntryWrapper): RangeEntryWrapper => {
            if (
                typeof accumEntry.value === 'string' ||
                typeof next.value === 'string' ||
                accumEntry.included !== next.included
            ) {
                return accumEntry
            }

            const nextValueTemplate: string = next.displayValue.replace(/\d/g, EMPTY_STRING)

            if (accumEntry.isRange) {
                const minValueTemplate: string = accumEntry.value.minDisplayValue.replace(/\d/g, EMPTY_STRING)

                if (nextValueTemplate !== minValueTemplate) {
                    return accumEntry
                }

                if (accumEntry.value.min - 1 === next.value) {
                    accumEntry.value.min = next.value
                    accumEntry.value.minDisplayValue = next.displayValue
                    isSequential = true
                } else if (accumEntry.value.max + 1 === next.value) {
                    accumEntry.value.max = next.value
                    accumEntry.value.maxDisplayValue = next.displayValue
                    isSequential = true
                } else if (next.value >= accumEntry.value.min && next.value <= accumEntry.value.max) {
                    isSequential = true
                }
            } else if (nextValueTemplate !== accumEntry.displayValue.replace(/\d/g, EMPTY_STRING)) {
                return accumEntry
            } else if (accumEntry.value - 1 === next.value) {
                isSequential = true
                return {
                    included: accumEntry.included,
                    isRange: true,
                    value: {
                        minDisplayValue: next.displayValue,
                        min: next.value,
                        maxDisplayValue: accumEntry.displayValue,
                        max: accumEntry.value
                    }
                }
            } else if (accumEntry.value + 1 === next.value) {
                isSequential = true
                return {
                    included: accumEntry.included,
                    isRange: true,
                    value: {
                        minDisplayValue: accumEntry.displayValue,
                        min: accumEntry.value,
                        maxDisplayValue: next.displayValue,
                        max: next.value
                    }
                }
            }

            return accumEntry
        })

        if (!isSequential) {
            mappedAccum.push(next)
        }

        return mappedAccum
    }, [] as RangeEntryWrapper[])
